import React, { useState } from 'react';
import { Upload, Download, AlertCircle, CheckCircle, X } from 'lucide-react';
import { useBrand } from '../../contexts/BrandContext';
import { useAuth } from '../../contexts/AuthContext';
import { forecastService } from '../../lib/forecastService';
import { useToast } from '../../contexts/ToastContext';

interface CSVImportProps {
  onClose: () => void;
  onSuccess: () => void;
}

const CSVImport: React.FC<CSVImportProps> = ({ onClose, onSuccess }) => {
  const { currentBrand } = useBrand();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [importType, setImportType] = useState<'clients' | 'expenses'>('clients');
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<any>(null);

  const downloadClientsTemplate = () => {
    const csvContent = `company_name,contact_name,contact_email,status,activation_date,notes
Acme Corporation,John Doe,john@acme.com,active,2025-01-01,New client from referral
Tech Startup Inc,Jane Smith,jane@techstartup.com,active,2025-01-15,
Consulting Group,Bob Johnson,bob@consulting.com,inactive,2024-06-01,Contract ended`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'clients_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    showToast('Template downloaded', 'success');
  };

  const downloadExpensesTemplate = () => {
    const csvContent = `expense_name,amount,expense_type,expense_date,recurrence_day,recurrence_start_date,status,payment_status,notes
Office Rent,3000.00,recurring,,15,2025-01-01,active,pending,Monthly office space
Software License,99.00,recurring,,1,2025-01-01,active,paid,Development tools
Marketing Campaign,5000.00,one_time,2025-02-15,,,active,pending,Q1 advertising
Equipment Purchase,2500.00,one_time,2025-03-01,,,active,pending,New laptops`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'expenses_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    showToast('Template downloaded', 'success');
  };

  const parseCSV = (text: string): any[] => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => h.trim());
    const records: any[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const record: any = {};
      headers.forEach((header, index) => {
        record[header] = values[index] || '';
      });
      records.push(record);
    }

    return records;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        showToast('Please select a CSV file', 'error');
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleImport = async () => {
    if (!file || !currentBrand || !user) return;

    try {
      setImporting(true);
      const text = await file.text();
      const records = parseCSV(text);

      if (records.length === 0) {
        showToast('No records found in CSV', 'error');
        return;
      }

      let importResult;
      if (importType === 'clients') {
        importResult = await forecastService.importClientsFromCSV(currentBrand.id, user.id, records);
      } else {
        importResult = await forecastService.importExpensesFromCSV(currentBrand.id, user.id, records);
      }

      setResult(importResult);
      if (importResult.records_imported > 0) {
        showToast(`Successfully imported ${importResult.records_imported} ${importType}`, 'success');
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      }
    } catch (error) {
      showToast('Failed to import CSV', 'error');
      console.error('Error importing CSV:', error);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Bulk Import from CSV</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Import Type</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setImportType('clients')}
                className={`p-4 border-2 rounded-lg text-center transition-all ${
                  importType === 'clients'
                    ? 'border-blue-600 bg-blue-50 text-blue-900'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <p className="font-semibold">Clients</p>
                <p className="text-xs text-gray-600 mt-1">Import client records</p>
              </button>
              <button
                onClick={() => setImportType('expenses')}
                className={`p-4 border-2 rounded-lg text-center transition-all ${
                  importType === 'expenses'
                    ? 'border-blue-600 bg-blue-50 text-blue-900'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <p className="font-semibold">Expenses</p>
                <p className="text-xs text-gray-600 mt-1">Import expense records</p>
              </button>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900">Download CSV Template First</p>
                <p className="text-sm text-blue-700 mt-1">
                  Download the template file, fill in your data, and then upload it back here for bulk import.
                </p>
                <button
                  onClick={importType === 'clients' ? downloadClientsTemplate : downloadExpensesTemplate}
                  className="mt-3 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  <Download className="w-4 h-4" />
                  Download {importType === 'clients' ? 'Clients' : 'Expenses'} Template
                </button>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Upload CSV File</label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
                id="csv-upload"
              />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-sm font-medium text-gray-900">
                  {file ? file.name : 'Click to upload CSV file'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {file ? 'Click to choose a different file' : 'or drag and drop'}
                </p>
              </label>
            </div>
          </div>

          {result && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Import Results</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Total Records:</span>
                  <span className="font-medium">{result.records_total}</span>
                </div>
                <div className="flex items-center justify-between text-green-600">
                  <span className="text-sm flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    Successfully Imported:
                  </span>
                  <span className="font-medium">{result.records_imported}</span>
                </div>
                {result.records_failed > 0 && (
                  <div className="flex items-center justify-between text-red-600">
                    <span className="text-sm flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      Failed:
                    </span>
                    <span className="font-medium">{result.records_failed}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
            <button
              onClick={handleImport}
              disabled={!file || importing}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {importing ? 'Importing...' : 'Import CSV'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CSVImport;